import { makeStyles } from "tss-react/mui"
import { Box, Stack } from "@mui/material"
import {
    AccountingDetailsCostCodes,
    MeetingServiceOrder,
} from "../../../../merchant/accounting/accounting/accountingTypes"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import {
    SectionBlock,
    SectionItemData,
    SectionItemLabel,
    SectionItemLink,
    SectionSubTitle,
} from "../../../../../shared/components/dialogs/receiptConfirmOrderDialog/components/common/ReceiptPrimitives"
import { BillReceiversType } from "../../../../../shared/components/billReceivers/billReceiverTypes"

const useStyles = makeStyles()((theme) => ({
    container: {
        background: tailwindPalette.emerald[50],
        [theme.breakpoints.down("sm")]: {
            background: "white",
            borderRadius: 0,
            paddingTop: 0,
            paddingBottom: "2rem",
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    wrap: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "4rem",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
        },
    },
    gridBox: {
        display: "grid",
        gridTemplateColumns: "9rem 1fr",
        gap: "1.5rem",
    },
}))

type BillReceiverProps = Readonly<{
    billReceiver: MeetingServiceOrder["buyerCustomerParty"]
    costCodes?: AccountingDetailsCostCodes[]
}>

export function BillReceiver({ billReceiver, costCodes }: BillReceiverProps) {
    const { classes } = useStyles()

    const renderContactInfo = () => {
        const contact = billReceiver.contactDetails
        // batch/details leaves the contact out
        if (!contact?.name && !contact?.email && !contact?.phoneNumber) return null

        return (
            <Box pt={2}>
                <SectionItemLabel translationKey="confirm-order-dialog-bill-receiver-contact">
                    Contact:
                </SectionItemLabel>
                {billReceiver.contactDetails?.name && (
                    <SectionItemData>{billReceiver.contactDetails.name}</SectionItemData>
                )}
                {billReceiver.contactDetails?.email && (
                    <SectionItemLink href={`mailto:${billReceiver.contactDetails.email}`}>
                        {billReceiver.contactDetails.email}
                    </SectionItemLink>
                )}
                {billReceiver.contactDetails?.phoneNumber && (
                    <SectionItemData>T: {billReceiver.contactDetails.phoneNumber}</SectionItemData>
                )}
            </Box>
        )
    }

    const renderEndpoint = () => {
        if (billReceiver.ean) {
            return <SectionItemData>EAN: {billReceiver.ean}</SectionItemData>
        }

        if (billReceiver.email) {
            return <SectionItemLink href={`mailto:${billReceiver.email}`}>{billReceiver.email}</SectionItemLink>
        }

        return null
    }

    const renderAddress = () => {
        const addressDisplayName =
            billReceiver.address?.displayName ??
            `${billReceiver.streetName} ${billReceiver.streetNumber}, ${billReceiver.postalCode} ${billReceiver.city}`
        return <SectionItemData>{addressDisplayName}</SectionItemData>
    }

    const renderDetails = () => {
        // Show CVR for companies and when the API omits `type`; hide only for the
        // types that intentionally don't display it (public institution / private person).
        const showVatNumber =
            billReceiver.type !== BillReceiversType.PUBLIC_INSTITUTION &&
            billReceiver.type !== BillReceiversType.PRIVATE_PERSON

        return (
            <>
                {showVatNumber && billReceiver.vatnumber && (
                    <SectionItemData>CVR: {billReceiver.vatnumber}</SectionItemData>
                )}
                {renderEndpoint()}
                {renderContactInfo()}
            </>
        )
    }

    return (
        <SectionBlock
            classNames={classes.container}
            translationKey="confirm-order-dialog-bill-receiver"
            title="Bill receiver"
        >
            <Box className={classes.wrap}>
                <Stack gap="0.5rem">
                    <SectionSubTitle>{billReceiver.name}</SectionSubTitle>
                    {renderAddress()}
                    {renderDetails()}
                </Stack>
                {!!costCodes?.length && (
                    <Stack gap=".5rem">
                        <SectionSubTitle translationKey="confirm-order-dialog-bill-receiver-cost-centers">
                            Cost centers:
                        </SectionSubTitle>
                        {costCodes.map((costCode) => (
                            <Box className={classes.gridBox}>
                                <SectionItemLabel>{costCode.dimensionName}</SectionItemLabel>
                                <SectionItemData textAlign="right">{costCode.name}</SectionItemData>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </SectionBlock>
    )
}
